import React, { useState, useMemo, useEffect } from 'react';
import BackWithSidebar from '../../../../components/BackWithSidebar';
import GeneralIcon from '../../../../assets';
import SearchBar from '../../../../components/SearchBar';
import AdicionarDocumento from './Documentos/AdicionarDocumento';
import DetalhesDocumento from './Documentos/DetalhesDocumento';
import documentService from '../../../../services/documentService';

export default function Documentos() {
  const [searchQuery, setSearchQuery] = useState('');
  const [documents, setDocuments] = useState([]);
  const [showFilters, setShowFilters] = useState(false);
  const [activeFilters, setActiveFilters] = useState({});
  const [showModal, setShowModal] = useState(false);
  const [documentToEdit, setDocumentToEdit] = useState(null);
  const [showDetails, setShowDetails] = useState(false);
  const [selectedDocument, setSelectedDocument] = useState(null);
  const [loading, setLoading] = useState(true);

  // Load documents on mount
  useEffect(() => {
    loadDocuments();
  }, []);

  async function loadDocuments() {
    try {
      setLoading(true);
      const docs = await documentService.fetchDocuments();
      setDocuments(docs);
    } catch (error) {
      console.error('Failed to load documents:', error);
    } finally {
      setLoading(false);
    }
  }
  
  const availableFilters = {
    tipo: ['Nota fiscal', 'Anotação', 'Termo', 'Quatro', 'Contrato de fornecimento', 'Contrato de produto', 'Relatório', 'Outro'],
  };

  function toggleFilter(key, value) {
    setActiveFilters(prev => {
      const current = prev[key] || [];
      const exists = current.includes(value);
      const next = exists ? current.filter(x => x !== value) : [...current, value];
      return { ...prev, [key]: next };
    });
  }

  function removeChip(key, value) {
    setActiveFilters(prev => {
      const current = prev[key] || [];
      const next = current.filter(x => x !== value);
      const copy = { ...prev };
      if (next.length) copy[key] = next;
      else delete copy[key];
      return copy;
    });
  }

  async function handleSaveDocument(doc) {
    try {
      if (documentToEdit) {
        await documentService.updateDocument(doc.id, doc);
      } else {
        await documentService.createDocument(doc);
      }
      await loadDocuments();
      setShowModal(false);
      setDocumentToEdit(null);
    } catch (error) {
      console.error('Failed to save document:', error);
      alert('Erro ao salvar documento');
    }
  }

  function handleEditDocument(doc) {
    setDocumentToEdit(doc);
    setShowModal(true);
  }

  async function handleDeleteDocument(id) {
    if (window.confirm('Tem certeza que deseja excluir este documento?')) {
      try {
        await documentService.deleteDocument(id);
        await loadDocuments();
      } catch (error) {
        console.error('Failed to delete document:', error);
        alert('Erro ao excluir documento');
      }
    }
  }

  function handleAddNew() {
    setDocumentToEdit(null);
    setShowModal(true);
  }

  function handleViewDetails(doc) {
    setSelectedDocument(doc);
    setShowDetails(true);
  }

  const filteredDocs = useMemo(() => {
    let result = [...documents];
    
    // Apply search
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      result = result.filter(doc => 
        doc.title?.toLowerCase().includes(q) || 
        doc.description?.toLowerCase().includes(q)
      );
    }
    
    // Apply filters
    if (activeFilters.tipo && activeFilters.tipo.length > 0) {
      result = result.filter(doc => activeFilters.tipo.includes(doc.tipo));
    }
    
    return result;
  }, [documents, searchQuery, activeFilters]);

  return (
    <div className="p-4 max-w-3xl mx-auto">
      <div className="flex items-center gap-3 mb-4">
        <BackWithSidebar to="/dashboard" className="p-2 rounded-full hover:bg-gray-100" />
        <h2 className="text-lg font-semibold">Documentos</h2>
      </div>

      <div className="bg-white rounded p-4 shadow-sm">
        {/* Search and Filter */}
        <div className="flex gap-3 items-center mb-3">
          <SearchBar
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            placeholder="Pesquisa de documentos"
            className="flex-1"
          />
          <div className="relative">
            <button
              onClick={() => setShowFilters(s => !s)}
              className="border rounded px-3 py-2 bg-gray-50 flex items-center gap-2 hover:bg-gray-100"
            >
              Filtrar
              <GeneralIcon iconName="filter" className="w-4 h-4" />
            </button>
            
            {showFilters && (
              <div className="absolute right-0 mt-2 w-64 bg-white border rounded shadow-lg p-3 z-20">
                {Object.entries(availableFilters).map(([key, options]) => (
                  <div key={key} className="mb-3">
                    <div className="text-xs font-medium text-gray-600 mb-2 capitalize">{key}</div>
                    <div className="flex flex-col gap-2">
                      {options.map(opt => (
                        <label key={opt} className="text-sm flex items-center gap-2 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={(activeFilters[key] || []).includes(opt)}
                            onChange={() => toggleFilter(key, opt)}
                            className="rounded"
                          />
                          <span>{opt}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                ))}
                <div className="text-right pt-2 border-t">
                  <button
                    onClick={() => setShowFilters(false)}
                    className="text-sm text-gray-600 hover:text-gray-800"
                  >
                    Fechar
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Active Filter Chips */}
        <div className="flex gap-2 flex-wrap mb-4">
          {Object.entries(activeFilters).flatMap(([k, arr]) =>
            arr.map(v => (
              <div
                key={`${k}:${v}`}
                className="bg-purple-100 text-purple-700 px-3 py-1 rounded-full text-sm flex items-center gap-2"
              >
                <span>{v}</span>
                <button
                  onClick={() => removeChip(k, v)}
                  className="text-purple-700 hover:text-purple-900"
                >
                  ×
                </button>
              </div>
            ))
          )}
        </div>


        {/* Results Count or Empty State */}
        {loading ? (
          <div className="text-center py-16 text-gray-500">Carregando...</div>
        ) : filteredDocs.length === 0 ? (
          <div className="text-center py-16">
            <GeneralIcon iconName="vazio" className="mx-auto mb-4 h-40 w-40 text-gray-300" />
            <div className="text-sm text-gray-500">Nenhum documento encontrado</div>
          </div>
        ) : (
          <>
            <div className="text-sm text-gray-600 mb-4">
              Encontramos {filteredDocs.length} resultado{filteredDocs.length !== 1 ? 's' : ''}
            </div>

            {/* Document List */}
            <div className="space-y-3">
              {filteredDocs.map(doc => (
                <div
                  key={doc.id}
                  onClick={() => handleViewDetails(doc)}
                  className="border rounded-lg p-4 hover:shadow-md transition cursor-pointer"
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <h3 className="font-semibold text-gray-900 mb-1">{doc.title}</h3>
                      <p className="text-sm text-gray-600 mb-2">{doc.description}</p>
                      <div className="inline-block px-2 py-1 bg-purple-50 text-purple-700 text-xs rounded">
                        {doc.tipo}
                      </div>
                    </div>
                    <div className="flex gap-2 ml-4">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleEditDocument(doc);
                        }}
                        className="p-2 rounded border border-purple-600 text-purple-600 hover:bg-purple-50"
                        title="Editar"
                      >
                        <GeneralIcon iconName="editar" className="w-4 h-4" />
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDeleteDocument(doc.id);
                        }}
                        className="p-2 rounded border border-red-500 text-red-500 hover:bg-red-50"
                        title="Excluir"
                      >
                        <GeneralIcon iconName="lixeira" className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}

        {/* Add New Document Button */}
        <button
          onClick={handleAddNew}
          className="w-full mt-6 py-3 rounded bg-purple-600 text-white font-medium hover:bg-purple-700 transition"
        >
          + Adicionar um novo documento
        </button>
      </div>

      {showModal && (
        <AdicionarDocumento
          document={documentToEdit}
          onClose={() => {
            setShowModal(false);
            setDocumentToEdit(null);
          }}
          onSave={handleSaveDocument}
        />
      )}

      {showDetails && selectedDocument && (
        <DetalhesDocumento
          document={selectedDocument}
          onClose={() => {
            setShowDetails(false);
            setSelectedDocument(null);
          }}
          onEdit={handleEditDocument}
          onDelete={handleDeleteDocument}
        />
      )}
    </div>
  );
}
